import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Web as WebIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useUserAccess } from '../../../context/UserAccessContext';
import MyAccountEditor from './MyAccountEditor';
import AdvisorProfileInfoEditor from './AdvisorProfileInfoEditor';
import AdvisorProfileEditor from './AdvisorProfileEditor';
import SipsAutomationPreferencesEditor from './SipsAutomationPreferencesEditor';
import EmailPreviewModal from './EmailPreviewModal';
import { useAdvisorConfig, useAdvisorConfigs } from '../../../hooks/useAdvisorConfig';
import { advisorDisplayLabel } from '../../../utils/advisorDisplay';

interface PreferencesModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Two clearly-separated groups of settings:
 *  - "My Account" — the USER you log in with (users collection / Auth0).
 *    Available to every role.
 *  - Advisor tabs — the ADVISOR CONFIGURATION(s) the user has rights to
 *    (advisorConfigs / SIPS preferences). Dashboard-advisors only; admins
 *    manage advisors via Customer Management instead.
 */
const PreferencesModal: React.FC<PreferencesModalProps> = ({
  open,
  onClose
}) => {
  const { role, customerIds, isAdmin, isDashboardAdmin } = useUserAccess();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(customerIds[0] || '');
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  // Load advisor config for email preview
  const { config } = useAdvisorConfig(selectedCustomerId || null, true);

  // All accessible advisor configs — labels the advisor picker with real
  // names + SIPS ids instead of raw internal customerIds.
  const { configs: accessibleConfigs } = useAdvisorConfigs();
  const advisorLabel = (customerId: string): string => advisorDisplayLabel(accessibleConfigs, customerId);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Advisor-configuration tabs only apply to dashboard-advisors; admins use
  // Customer Management and planners have no advisor access.
  const showAdvisorTabs = !isAdmin && !isDashboardAdmin && role !== 'planner';

  // Shared banner making it explicit WHICH entity a tab edits.
  const userAccountBanner = (
    <Alert severity="info" icon={<AccountCircleIcon fontSize="inherit" />} sx={{ mb: 2 }}>
      <strong>Your login account.</strong> These settings belong to the user you sign in
      with — they are independent of any advisor configuration.
    </Alert>
  );
  const advisorConfigBanner = (
    <Alert severity="warning" icon={<PersonIcon fontSize="inherit" />} sx={{ mb: 2 }}>
      <strong>Advisor configuration.</strong> These settings belong to the selected
      advisor profile you have rights to — client-facing branding, intake, and
      automation. They do not change your login account.
    </Alert>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Preferences
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<AccountCircleIcon />} iconPosition="start" label="My Account" />
              {showAdvisorTabs && (
                <Tab icon={<PersonIcon />} iconPosition="start" label="Advisor Profile" />
              )}
              {showAdvisorTabs && (
                <Tab icon={<WebIcon />} iconPosition="start" label="Web Client Interview" />
              )}
              {showAdvisorTabs && (
                <Tab icon={<AssessmentIcon />} iconPosition="start" label="Planning" />
              )}
            </Tabs>

            {/* Advisor picker — one selection drives ALL advisor tabs (profile,
                web interview, planning). Shown only on advisor tabs; a dropdown
                when the user manages multiple advisors, a static label otherwise. */}
            {showAdvisorTabs && activeTab > 0 && customerIds.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  mt: 2,
                  bgcolor: 'action.hover'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Editing advisor:
                </Typography>
                {customerIds.length > 1 ? (
                  <FormControl size="small" sx={{ minWidth: 320 }}>
                    <InputLabel>Advisor</InputLabel>
                    <Select
                      value={selectedCustomerId}
                      label="Advisor"
                      onChange={(e) => setSelectedCustomerId(e.target.value as string)}
                    >
                      {customerIds.map((id) => (
                        <MenuItem key={id} value={id}>
                          {advisorLabel(id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body2">{advisorLabel(selectedCustomerId)}</Typography>
                )}
                {customerIds.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Applies to the Advisor Profile, Web Client Interview, and Planning tabs.
                  </Typography>
                )}
              </Paper>
            )}

            {/* Tab Panel: My Account (the USER you log in with) */}
            {activeTab === 0 && (
              <Box sx={{ pt: 3 }}>
                {userAccountBanner}
                <MyAccountEditor />
                {(isAdmin || isDashboardAdmin) && (
                  <Alert severity="info" sx={{ mt: 3 }}>
                    Advisor configurations are managed on the Customer Management page.
                  </Alert>
                )}
              </Box>
            )}

            {showAdvisorTabs && customerIds.length === 0 && activeTab > 0 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <strong>No Customer IDs Assigned:</strong> You don't have any advisor
                configurations assigned to your account. Contact your administrator to get access.
              </Alert>
            )}

            {/* Tab Panel: Advisor Profile Information */}
            {showAdvisorTabs && customerIds.length > 0 && activeTab === 1 && (
              <Box sx={{ pt: 3 }}>
                {advisorConfigBanner}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Manage the advisor's profile information, photos, and branding.
                </Typography>
                <AdvisorProfileInfoEditor
                  selectedCustomerId={selectedCustomerId}
                  onCustomerIdChange={setSelectedCustomerId}
                />
              </Box>
            )}

            {/* Tab Panel: Web Client Interview Preferences */}
            {showAdvisorTabs && customerIds.length > 0 && activeTab === 2 && (
              <Box sx={{ pt: 3 }}>
                {advisorConfigBanner}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure how client interview submissions are processed and customize the welcome message.
                </Typography>

                {/* Email Preview Button */}
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setEmailPreviewOpen(true)}
                    disabled={!selectedCustomerId}
                  >
                    Preview Email Templates
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    See what clients will receive after submitting the form
                  </Typography>
                </Box>

                <AdvisorProfileEditor
                  selectedCustomerId={selectedCustomerId}
                  onCustomerIdChange={setSelectedCustomerId}
                />
              </Box>
            )}

            {/* Tab Panel: Planning Preferences */}
            {showAdvisorTabs && customerIds.length > 0 && activeTab === 3 && (
              <Box sx={{ pt: 3 }}>
                {advisorConfigBanner}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure SIPS automation preferences and planning parameters.
                </Typography>
                {selectedCustomerId ? (
                  <SipsAutomationPreferencesEditor customerId={selectedCustomerId} />
                ) : (
                  <Alert severity="info">
                    Please select a customer ID to view planning preferences.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        advisorConfig={config ? {
          name: config.name,
          company: config.company,
          email: config.email,
          automationWelcomeMessage: config.features?.branding?.automationWelcomeMessage,
          customerId: config.customerId
        } : null}
      />
    </>
  );
};

export default PreferencesModal;
